export enum UserRole {
  GUEST = 'GUEST',
  USER = 'USER',
  ADMIN = 'ADMIN',
}

// Auth Types
export type RefreshTokenArgs = {
  refreshToken: string;
};

export type AuthResponse = {
  accessToken: string;
  refreshToken: string;
};

export type LoginArgs = {
  email: string;
  password: string;
};

export type RegisterArgs = {
  firstName: string;
  lastName?: string;
  email: string;
  password: string;
  language?: string;
};

export type ConfirmEmailArgs = {
  code: string;
};

export type RequestPasswordResetArgs = {
  email: string;
};

export type ResetPasswordArgs = {
  resetToken: string;
  newPassword: string;
};

export type GoogleAuthLoginArgs = {
  token: string;
  accessToken: string;
};

export type GoogleAuthResponse = {
  accessToken: string;
  refreshToken: string;
  jwtId: string;
  isExistingUser: boolean;
};

export type GoogleAuthRegisterArgs = {
  token?: string;
  accessToken?: string;
  language?: string;
};

export type SuccessResponse = {
  success: boolean;
};

// User Types
export type UserResponse = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  isActive: boolean;
  isConfirmed: boolean;
  isWhitelisted: boolean;
  language: string;
  createdAt: string;
  updatedAt: string;
};

export type UpdateUserArgs = {
  firstName?: string;
  lastName?: string;
};
// AI Types
export type CreateSessionArgs = {
  appName: string;
};

export type SessionResponse = {
  id: string;
  appName: string;
  userId: string;
  state: {
    chatId: string;
    title: string;
  } & Record<string, string>;
  events: AIEvent[];
  lastUpdateTime: number;
  meta: {
    id: string;
    title: string;
    userId: string;
    app: string;
    sessionId: string;
    meta: Record<string, string>;
    createdAt: string;
    updatedAt: string;
  };
};

export type GetSessionArgs = {
  sessionId: string;
};

export type DeleteSessionArgs = {
  sessionId: string;
};

export type EditSessionTitleArgs = {
  sessionId: string;
  title: string;
};

export type RunAgentArgs = {
  sessionId: string;
  text: string;
};

export type AgentResponse = {
  events: AIEvent[];
  meta: {
    id: string;
    title: string;
    userId: string;
    app: string;
    sessionId: string;
    meta: Record<string, string>;
    createdAt: string;
    updatedAt: string;
  };
};

export type AIEventUserMessage = {
  text: string;
  thoughtSignature?: string;
  role: 'user';
  id: string;
};

export type AIEventAgentResponse = {
  text: string;
  thoughtSignature?: string;
  role: 'model';
  id: string;
};

export type AIEventAgentFunctionCall = {
  functionCall: {
    id: string;
    args: Record<string, any>;
    name: string;
  };
  text?: never;
  role: 'model';
  id: string;
};

export type AIEventAgentFunctionResponse = {
  functionResponse: {
    id: string;
    name: string;
    response: Record<string, any>;
  };
  text?: never;
  role: 'model';
  id: string;
};

export type AIEvent = {
  id: string;
  invocationId: string;
  content: {
    role: 'user' | 'model';
    parts: (
      | Omit<AIEventUserMessage, 'role' | 'id'>
      | Omit<AIEventAgentResponse, 'role' | 'id'>
      | Omit<AIEventAgentFunctionResponse, 'role' | 'id'>
      | Omit<AIEventAgentFunctionCall, 'role' | 'id'>
    )[];
  };
  partial?: boolean;
  errorCode?: string;
  timestamp: number;
  author: string;
  actions: {
    staleDelta: {};
    artifactDelta: {};
    requestedAuthConfigs: {};
  };
  usageMetadata?: {
    candidatesTokenCount: number;
    promptTokenCount: number;
    proptTokenDetails: {
      modadlity: string;
      tokenCount: number;
    }[];
    thoughtsTokenCount: number;
    totalTokenCount: number;
  };
};

export type ApplyForAnWhitelistArgs = {
  industry: string;
  contact: string;
};

export type UpdateUserLanguageArgs = {
  language: string;
};
